import "./CreateNotification.scss";
import AddPhoto from "./../../../assets/add-photo.png";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { CreateNotificationsColumns } from "../../../SourceData";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchNotificationTargets,
  setPage,
  setPageSize,
  setQuery,
  setCurrentItemType,
  setShowDataGrid,
  setSelectedItemIds,
  resetTargets,
} from "../../../redux/notificationsSlice";

const CreateNotification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    items,
    loading: itemsLoading,
    page,
    pageSize,
    totalItems,
    query,
    currentItemType,
    showDataGrid,
    selectedItemIds,
  } = useSelector((state) => state.notifications);

  const [formData, setFormData] = useState({
    title_en: "",
    title_ar: "",
    body_en: "",
    body_ar: "",
    sender_type: "all",
    image: null,
    receiver_id: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (showDataGrid && currentItemType) {
      dispatch(fetchNotificationTargets({ page, pageSize, query, itemType: currentItemType }));
    }
  }, [dispatch, page, pageSize, query, currentItemType, showDataGrid]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "sender_type") {
      dispatch(resetTargets());

      const showGrid = [
        "specific-users",
        "specific-shops",
        "specific-factories",
      ].includes(value);

      if (showGrid) {
        dispatch(setShowDataGrid(true));
        dispatch(setCurrentItemType(value));
      }
    }

    setFormData({
      ...formData,
      [name]: value,
      ...(name === "sender_type" ? { receiver_id: [] } : {}),
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleItemSelection = (newSelection) => {
    dispatch(setSelectedItemIds(newSelection));
    setFormData({ ...formData, receiver_id: newSelection });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      ["specific-users", "specific-shops", "specific-factories"].includes(
        formData.sender_type
      ) &&
      selectedItemIds.length === 0
    ) {
      toast.error("يجب اختيار مستلم واحد على الأقل");
      return;
    }

    try {
      setSubmitting(true);
      const submitData = new FormData();

      submitData.append("title[en]", formData.title_en);
      submitData.append("title[ar]", formData.title_ar);
      submitData.append("body[en]", formData.body_en);
      submitData.append("body[ar]", formData.body_ar);

      let apiSenderType;
      switch (formData.sender_type) {
        case "specific-users":
          apiSenderType = "user";
          break;
        case "specific-shops":
          apiSenderType = "shop";
          break;
        case "specific-factories":
          apiSenderType = "factory";
          break;
        default:
          apiSenderType = formData.sender_type;
      }

      submitData.append("sender_type", apiSenderType);

      if (formData.image) {
        submitData.append("image", formData.image);
      }

      if (
        ["specific-users", "specific-shops", "specific-factories"].includes(
          formData.sender_type
        )
      ) {
        selectedItemIds.forEach((id) => {
          submitData.append("receiver_id[]", id);
        });
      }

      await api.post("/notifications", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("تم إنشاء الإشعار بنجاح");
      dispatch(resetTargets());

      setTimeout(() => {
        navigate("/notifications");
      }, 2000);
    } catch (error) {
      console.error("Error creating notification:", error);
      toast.error("فشل في إنشاء الإشعار");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-notification">
      <div className="special-header">
        <p className="title">اضافة اشعار جديد</p>
        <div className="form-img">
          <label htmlFor="image" className="img-control">
            <img src={imagePreview || AddPhoto} alt="" />
            <span>اضافة صورة الشعار</span>
          </label>
          <input type="file" id="image" onChange={handleImageChange} />
        </div>
      </div>

      <div className="notification-container">
        <form onSubmit={handleSubmit}>
          <div className="control-group">
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="title_ar"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                عنوان النص بالعربي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="title_ar"
                name="title_ar"
                value={formData.title_ar}
                onChange={handleChange}
                type="text"
                notched
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="title_en"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                عنوان النص بالانجليزي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="title_en"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                type="text"
                notched
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="body_ar"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                النص بالعربي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="body_ar"
                name="body_ar"
                value={formData.body_ar}
                onChange={handleChange}
                type="text"
                notched
                multiline
                minRows={5}
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="body_en"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                النص بالانجليزي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="body_en"
                name="body_en"
                value={formData.body_en}
                onChange={handleChange}
                type="text"
                notched
                multiline
                minRows={5}
              />
            </FormControl>
          </div>

          <FormControl fullWidth>
            <InputLabel
              htmlFor="sender_type"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
                textAlign: "right",
              }}
            >
              حدد المستلم
            </InputLabel>
            <Select
              labelId="sender_type-label"
              id="sender_type"
              name="sender_type"
              value={formData.sender_type}
              onChange={handleChange}
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="user">المستخدمين</MenuItem>
              <MenuItem value="shop">المتاجر</MenuItem>
              <MenuItem value="factory">المصانع</MenuItem>
              <MenuItem value="specific-users">تحديد مستخدمين</MenuItem>
              <MenuItem value="specific-shops">تحديد متاجر</MenuItem>
              <MenuItem value="specific-factories">تحديد مصانع</MenuItem>
            </Select>
          </FormControl>

          <BlueButton
            text={submitting ? "جاري الإرسال..." : "ارسال"}
            size="medium"
            variant="fill"
            type="submit"
            disabled={submitting}
          />
        </form>

        {showDataGrid && (
          <div className="left">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث..."
              onChange={(e) => dispatch(setQuery(e.target.value))}
              sx={{ width: "100%" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {itemsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <div className="datagrid-scroll-wrapper">
                <DataGrid
                  sx={{ backgroundColor: "white", width: "100%" }}
                  columns={CreateNotificationsColumns}
                  rows={items}
                  disableColumnFilter
                  disableColumnMenu
                  checkboxSelection
                  disableRowSelectionOnClick
                  onRowSelectionModelChange={handleItemSelection}
                  rowSelectionModel={selectedItemIds}
                  paginationMode="server"
                  rowCount={totalItems}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    dispatch(setPage(model.page));
                    dispatch(setPageSize(model.pageSize));
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  pagination
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateNotification;
